import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import NotificationToast, { NotificationData, NotificationType } from '../components/ui/NotificationToast';

interface NotificationContextType {
  notifications: NotificationData[];
  addNotification: (notification: Omit<NotificationData, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  showSuccess: (title: string, message: string, action?: NotificationData['action']) => void;
  showError: (title: string, message: string, action?: NotificationData['action']) => void;
  showWarning: (title: string, message: string, action?: NotificationData['action']) => void;
  showInfo: (title: string, message: string, action?: NotificationData['action']) => void;
  showMatch: (title: string, message: string, avatar?: string, action?: NotificationData['action']) => void;
  showMessage: (title: string, message: string, avatar?: string, action?: NotificationData['action']) => void;
  showUpgrade: (title: string, message: string, action?: NotificationData['action']) => void;
  showSuperFuck: (title: string, message: string, avatar?: string, action?: NotificationData['action']) => void;
  showSuperMarry: (title: string, message: string, avatar?: string, action?: NotificationData['action']) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  maxNotifications = 5 
}) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const generateId = () => `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addNotification = useCallback((notification: Omit<NotificationData, 'id' | 'timestamp'>) => {
    const newNotification: NotificationData = {
      ...notification,
      id: generateId(),
      timestamp: new Date()
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      // Keep only the most recent notifications
      return updated.slice(0, maxNotifications);
    });
  }, [maxNotifications]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods for common notification types
  const showSuccess = useCallback((title: string, message: string, action?: NotificationData['action']) => {
    addNotification({ type: 'success', title, message, action, duration: 4000 });
  }, [addNotification]);

  const showError = useCallback((title: string, message: string, action?: NotificationData['action']) => {
    addNotification({ type: 'error', title, message, action, duration: 6000 });
  }, [addNotification]);

  const showWarning = useCallback((title: string, message: string, action?: NotificationData['action']) => {
    addNotification({ type: 'warning', title, message, action, duration: 5000 });
  }, [addNotification]);

  const showInfo = useCallback((title: string, message: string, action?: NotificationData['action']) => {
    addNotification({ type: 'info', title, message, action, duration: 4000 });
  }, [addNotification]);

  const showMatch = useCallback((title: string, message: string, avatar?: string, action?: NotificationData['action']) => {
    addNotification({ 
      type: 'match', 
      title, 
      message, 
      avatar, 
      action, 
      duration: 8000 // Longer duration for important match notifications
    });
  }, [addNotification]);

  const showMessage = useCallback((title: string, message: string, avatar?: string, action?: NotificationData['action']) => {
    addNotification({ type: 'message', title, message, avatar, action, duration: 5000 });
  }, [addNotification]);

  const showUpgrade = useCallback((title: string, message: string, action?: NotificationData['action']) => {
    addNotification({ 
      type: 'upgrade', 
      title, 
      message, 
      action, 
      duration: 7000 // Longer duration for upgrade prompts
    });
  }, [addNotification]);

  const showSuperFuck = useCallback((title: string, message: string, avatar?: string, action?: NotificationData['action']) => {
    addNotification({ 
      type: 'super-fuck', 
      title, 
      message, 
      avatar, 
      action, 
      duration: 8000 // Longer duration for important super notifications
    });
  }, [addNotification]);

  const showSuperMarry = useCallback((title: string, message: string, avatar?: string, action?: NotificationData['action']) => {
    addNotification({ 
      type: 'super-marry', 
      title, 
      message, 
      avatar, 
      action, 
      duration: 8000 // Longer duration for important super notifications
    });
  }, [addNotification]);

  const contextValue: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showMatch,
    showMessage,
    showUpgrade,
    showSuperFuck,
    showSuperMarry
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      
      {/* Notification Container */}
      <div className="fixed top-4 right-4 z-[9999] space-y-2 pointer-events-none">
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onClose={removeNotification}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Custom hook for handling real-time messaging notifications
export const useMessageNotifications = () => {
  const { showMessage, showMatch } = useNotifications();

  const notifyNewMessage = useCallback((senderName: string, messagePreview: string, avatar?: string) => {
    showMessage(
      `New message from ${senderName}`,
      messagePreview,
      avatar,
      {
        label: 'Reply',
        onClick: () => {
          // Navigate to chat - would be implemented based on routing setup
          console.log('Navigate to chat');
        }
      }
    );
  }, [showMessage]);

  const notifyNewMatch = useCallback((matchName: string, avatar?: string) => {
    showMatch(
      'New Match! 🎉',
      `You and ${matchName} liked each other`,
      avatar,
      {
        label: 'Say Hi',
        onClick: () => {
          // Navigate to chat with new match
          console.log('Navigate to new match chat');
        }
      }
    );
  }, [showMatch]);

  return {
    notifyNewMessage,
    notifyNewMatch
  };
};

// Custom hook for subscription-related notifications
export const useSubscriptionNotifications = () => {
  const { showUpgrade, showSuccess, showWarning } = useNotifications();

  const notifyFeatureBlocked = useCallback((featureName: string, requiredTier: 'premium' | 'platinum') => {
    showUpgrade(
      `${featureName} requires ${requiredTier}`,
      `Upgrade to ${requiredTier} to unlock this feature`,
      {
        label: 'Upgrade Now',
        onClick: () => {
          // Open upgrade modal
          console.log('Open upgrade modal');
        }
      }
    );
  }, [showUpgrade]);

  const notifyUpgradeSuccess = useCallback((tier: string) => {
    showSuccess(
      'Upgrade Successful! 🎉',
      `Welcome to ${tier}! All features are now unlocked.`
    );
  }, [showSuccess]);

  const notifyUsageLimitReached = useCallback((limitType: string, resetTime: string) => {
    showWarning(
      'Daily Limit Reached',
      `You've reached your daily ${limitType} limit. Resets ${resetTime}.`,
      {
        label: 'Upgrade',
        onClick: () => {
          console.log('Open upgrade modal');
        }
      }
    );
  }, [showWarning]);

  return {
    notifyFeatureBlocked,
    notifyUpgradeSuccess,
    notifyUsageLimitReached
  };
};

// Custom hook for super-like notifications
export const useSuperLikeNotifications = () => {
  const { showSuperFuck, showSuperMarry } = useNotifications();

  const notifySuperFucked = useCallback((senderName: string, avatar?: string) => {
    showSuperFuck(
      'You got Super Fucked! 🔥',
      `${senderName} thinks you're smoking hot!`,
      avatar,
      {
        label: 'Say Hi',
        onClick: () => {
          // Navigate to chat with super fucker
          console.log('Navigate to super fuck chat');
        }
      }
    );
  }, [showSuperFuck]);

  const notifySuperMarried = useCallback((senderName: string, avatar?: string) => {
    showSuperMarry(
      'You got Super Married! 💚',
      `${senderName} wants to wife you up!`,
      avatar,
      {
        label: 'Say Hi',
        onClick: () => {
          // Navigate to chat with super marrier
          console.log('Navigate to super marry chat');
        }
      }
    );
  }, [showSuperMarry]);

  return {
    notifySuperFucked,
    notifySuperMarried
  };
};

export default NotificationContext;